import React, { useEffect, useMemo } from "react"

import { useFormContext } from "react-hook-form"

import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"

import { CheckboxElement } from "@front-shared/elements/checkbox.element"
import { InputErrorElement } from "@front-shared/elements/inputError.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { CHECKER_CONFIG } from "../../../../config/checker.config"
import type { IDynamicCheckerFormInputData } from "../../../../types/data.types"

export const NetworkInput: React.FunctionComponent<
    IDynamicFormNetworkInputProps
> = props => {
    const { setValue, watch, register, unregister } =
        useFormContext<IDynamicCheckerFormInputData>()

    const dataWatcher = watch()

    const isInNetworkOnlyCheckType = useMemo<boolean>(() => {
        return (
            !!props.manualCheckType &&
            CHECKER_CONFIG.inNetworkOnlyCheckTypes.includes(
                props.manualCheckType,
            )
        )
    }, [props.manualCheckType])

    const isDisabled = useMemo<boolean>(() => {
        return !!props.disabled || isInNetworkOnlyCheckType
    }, [props.disabled, isInNetworkOnlyCheckType])

    // Monitor input predefined data networks and make sure properly set them in state
    useEffect(() => {
        typeof props.firstCheckInputInState?.inNetworkCheck !== "undefined" &&
            setValue(
                "inNetworkCheck",
                props.firstCheckInputInState?.inNetworkCheck,
            )

        typeof props.firstCheckInputInState?.outNetworkCheck !== "undefined" &&
            setValue(
                "outNetworkCheck",
                props.firstCheckInputInState?.outNetworkCheck,
            )
    }, [
        props.firstCheckInputInState?.inNetworkCheck,
        props.firstCheckInputInState?.outNetworkCheck,
    ])

    // Monitor isInNetworkOnlyCheckType and set needed default values
    useEffect(() => {
        if (!isInNetworkOnlyCheckType) {
            return
        }

        setValue("inNetworkCheck", true)
        setValue("outNetworkCheck", false)
    }, [isInNetworkOnlyCheckType])

    return (
        <div
            className={`
                relative flex
                ${props.className}
            `}
        >
            <CheckboxElement
                className="mr-[40px]"
                checkboxClassName="!flex !items-start"
                name="inNetworkCheck"
                reactHookFormRegister={register as any}
                reactHookFormUnregister={unregister as any}
                reactHookFormSet={setValue as any}
                reactHookValidations={{
                    validate: () => {
                        const localDataWatcher = watch()

                        return (
                            localDataWatcher?.inNetworkCheck ||
                            localDataWatcher?.outNetworkCheck ||
                            "Network is required"
                        )
                    },
                }}
                disabled={isDisabled}
                watchDefaultCheckedChange
                defaultChecked={
                    typeof dataWatcher.inNetworkCheck === "undefined"
                        ? true
                        : dataWatcher.inNetworkCheck
                }
                label={<PrimaryText>INN</PrimaryText>}
            />

            <CheckboxElement
                checkboxClassName="!flex !items-start"
                name="outNetworkCheck"
                reactHookFormRegister={register as any}
                reactHookFormUnregister={unregister as any}
                reactHookFormSet={setValue as any}
                disabled={isDisabled}
                watchDefaultCheckedChange
                defaultChecked={dataWatcher.outNetworkCheck}
                label={<PrimaryText>OON</PrimaryText>}
            />

            {!dataWatcher.outNetworkCheck && !dataWatcher.inNetworkCheck ? (
                <InputErrorElement
                    className="absolute left-0 top-[24px]"
                    text="Network is required"
                />
            ) : null}
        </div>
    )
}

interface IDynamicFormNetworkInputProps {
    className?: string
    disabled?: boolean
    manualCheckType?: QuotaUsageType
    firstCheckInputInState?: IDynamicCheckerFormInputData
}
